import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Check, X, Camera, CaretDown, CaretUp, Plus, MusicNote, SpeakerHigh } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DJEntertainmentSelector = ({ onSave }) => {
  const [djTypes, setDjTypes] = useState([]);
  const [equipmentItems, setEquipmentItems] = useState([]);
  const [genres, setGenres] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [uploadTarget, setUploadTarget] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/quick-add-items?vendor_category=dj`);
      setDjTypes(data.filter(i => i.item_type === 'dj_type'));
      setEquipmentItems(data.filter(i => i.item_type === 'equipment'));
      setGenres(data.filter(i => i.item_type === 'genre'));
    } catch { /* fallback */ }
    setLoaded(true);
  };

  const makeEmptyType = (name, defaultPrice) => ({
    name,
    expanded: true,
    photos: [],
    price_per_event: defaultPrice || '',
    price_per_hour: '',
    num_performers: 1,
    specialization: '',
    selectedEquipment: [],
    selectedGenres: [],
  });

  const toggleType = (dt) => {
    const exists = selectedTypes.find(t => t.name === dt.name);
    if (exists) {
      setSelectedTypes(selectedTypes.filter(t => t.name !== dt.name));
    } else {
      setSelectedTypes([...selectedTypes, makeEmptyType(dt.name, dt.defaultPrice)]);
    }
  };

  const toggleExpand = (idx) => {
    const u = [...selectedTypes];
    u[idx].expanded = !u[idx].expanded;
    setSelectedTypes(u);
  };

  const updateField = (idx, field, value) => {
    const u = [...selectedTypes];
    u[idx][field] = value;
    setSelectedTypes(u);
  };

  const toggleEquipment = (typeIdx, eqName, eqPrice) => {
    const u = [...selectedTypes];
    const list = u[typeIdx].selectedEquipment;
    const exists = list.find(e => e.name === eqName);
    if (exists) {
      u[typeIdx].selectedEquipment = list.filter(e => e.name !== eqName);
    } else {
      u[typeIdx].selectedEquipment = [...list, { name: eqName, price: eqPrice, included: true }];
    }
    setSelectedTypes(u);
  };

  const toggleEquipmentIncluded = (typeIdx, eqIdx) => {
    const u = [...selectedTypes];
    u[typeIdx].selectedEquipment[eqIdx].included = !u[typeIdx].selectedEquipment[eqIdx].included;
    setSelectedTypes(u);
  };

  const toggleGenre = (typeIdx, genreName) => {
    const u = [...selectedTypes];
    const list = u[typeIdx].selectedGenres;
    if (list.includes(genreName)) {
      u[typeIdx].selectedGenres = list.filter(g => g !== genreName);
    } else {
      u[typeIdx].selectedGenres = [...list, genreName];
    }
    setSelectedTypes(u);
  };

  // Photo upload (max 3)
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !uploadTarget) return;
    setUploading(uploadTarget.key);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const resp = await axios.post(`${BACKEND_URL}/api/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      const url = resp.data.url;
      const { tIdx } = uploadTarget;
      const updated = [...selectedTypes];
      if (updated[tIdx].photos.length < 3) {
        updated[tIdx].photos.push(url);
        setSelectedTypes(updated);
        toast.success('Photo uploaded!');
      }
    } catch {
      toast.error('Upload failed');
    }
    setUploading(null);
    setUploadTarget(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePhoto = (tIdx, pIdx) => {
    const u = [...selectedTypes];
    u[tIdx].photos = u[tIdx].photos.filter((_, i) => i !== pIdx);
    setSelectedTypes(u);
  };

  const triggerUpload = (target) => {
    setUploadTarget(target);
    setTimeout(() => fileInputRef.current?.click(), 50);
  };

  const handleSaveAll = () => {
    if (selectedTypes.length === 0) {
      toast.error('Select at least one entertainment type');
      return;
    }
    onSave({ entertainment_types: selectedTypes });
  };

  if (!loaded) return <div className="text-center py-8 text-text-secondary">Loading entertainment types...</div>;

  return (
    <div className="space-y-3">
      <input type="file" accept="image/*" ref={fileInputRef} onChange={handlePhotoUpload} className="hidden" />
      <p className="text-sm text-text-secondary">Select entertainment types you offer. Add photos, pricing, equipment & genre details.</p>

      {djTypes.map((dt, idx) => {
        const selected = selectedTypes.find(t => t.name === dt.name);
        const tIdx = selectedTypes.findIndex(t => t.name === dt.name);

        return (
          <div key={idx} className={`border-2 rounded-md transition-all ${selected ? 'border-green-500 bg-green-50/30' : 'border-[#E8E1D5] bg-white'}`}>
            {/* Type header */}
            <div className="flex items-center gap-3 p-3" data-testid={`toggle-dj-${dt.name.toLowerCase().replace(/[ /]/g, '-')}`}>
              <Button
                type="button" onClick={() => toggleType(dt)}
                className={`rounded-full w-10 h-10 p-0 flex-shrink-0 ${selected ? 'bg-green-600 hover:bg-green-700' : 'bg-red-500 hover:bg-red-600'} text-white`}
              >
                {selected ? <Check size={18} weight="bold" /> : <X size={18} weight="bold" />}
              </Button>
              <div className="flex-1 font-medium cursor-pointer" onClick={() => selected && toggleExpand(tIdx)}>
                {dt.name}
                {selected && <span className="text-xs text-text-secondary ml-2">({selected.photos.length} photos)</span>}
              </div>
              {dt.defaultPrice > 0 && !selected && (
                <span className="text-sm font-semibold text-brand-primary">₹{dt.defaultPrice.toLocaleString('en-IN')}</span>
              )}
              {selected && (
                <Button type="button" variant="ghost" size="sm" onClick={() => toggleExpand(tIdx)}>
                  {selected.expanded ? <CaretUp size={16} /> : <CaretDown size={16} />}
                </Button>
              )}
            </div>

            {/* Expanded content */}
            {selected && selected.expanded && (
              <div className="border-t border-[#E8E1D5] px-4 py-4 space-y-5 bg-white/50">

                {/* Photos (up to 3) */}
                <div>
                  <div className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-2">Photos (up to 3)</div>
                  <div className="flex flex-wrap gap-2">
                    {selected.photos.map((photo, pIdx) => (
                      <div key={pIdx} className="relative group">
                        <div className="w-20 h-20 border border-[#E8E1D5] rounded overflow-hidden">
                          <img src={`${BACKEND_URL}${photo}`} alt="" className="w-full h-full object-cover" />
                        </div>
                        <button
                          type="button"
                          onClick={() => removePhoto(tIdx, pIdx)}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X size={10} weight="bold" />
                        </button>
                      </div>
                    ))}
                    {selected.photos.length < 3 && (
                      <div
                        className="w-20 h-20 border-2 border-dashed border-brand-primary/30 rounded flex flex-col items-center justify-center cursor-pointer hover:border-brand-primary hover:bg-brand-accent/5 transition-all"
                        onClick={() => triggerUpload({ key: `dj-${tIdx}`, tIdx })}
                        data-testid={`add-photo-dj-${tIdx}`}
                      >
                        <Plus size={18} className="text-brand-primary mb-1" />
                        <span className="text-[10px] text-text-secondary">Add</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Pricing & Performers */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs font-bold text-text-secondary">Price per Event (₹)</Label>
                    <Input
                      type="number"
                      value={selected.price_per_event}
                      onChange={(e) => updateField(tIdx, 'price_per_event', e.target.value)}
                      placeholder="₹ per event"
                      className="rounded-none border-[#E8E1D5] h-9"
                      data-testid={`price-event-${tIdx}`}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-bold text-text-secondary">Price per Hour (₹)</Label>
                    <Input
                      type="number"
                      value={selected.price_per_hour}
                      onChange={(e) => updateField(tIdx, 'price_per_hour', e.target.value)}
                      placeholder="₹ per hour"
                      className="rounded-none border-[#E8E1D5] h-9"
                      data-testid={`price-hour-${tIdx}`}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-bold text-text-secondary">No. of Performers</Label>
                    <div className="flex items-center gap-2">
                      <Button type="button" variant="outline" size="sm" className="w-8 h-8 p-0 rounded-none border-[#E8E1D5]"
                        onClick={() => updateField(tIdx, 'num_performers', Math.max(1, selected.num_performers - 1))}>
                        <span className="text-lg">-</span>
                      </Button>
                      <span className="w-8 text-center font-bold" data-testid={`performers-count-${tIdx}`}>{selected.num_performers}</span>
                      <Button type="button" variant="outline" size="sm" className="w-8 h-8 p-0 rounded-none border-[#E8E1D5]"
                        onClick={() => updateField(tIdx, 'num_performers', selected.num_performers + 1)}>
                        <span className="text-lg">+</span>
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Specialization */}
                <div className="space-y-1">
                  <Label className="text-xs font-bold text-text-secondary">Specialization / Notes</Label>
                  <Input
                    type="text"
                    value={selected.specialization}
                    onChange={(e) => updateField(tIdx, 'specialization', e.target.value)}
                    placeholder="e.g. Wedding specialist, Corporate events..."
                    className="rounded-none border-[#E8E1D5] h-9"
                    data-testid={`specialization-${tIdx}`}
                  />
                </div>

                {/* Equipment */}
                {equipmentItems.length > 0 && (
                  <div>
                    <div className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-2 flex items-center gap-1">
                      <SpeakerHigh size={14} /> Equipment
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {equipmentItems.map((eq, eIdx) => {
                        const isSelected = selected.selectedEquipment.find(e => e.name === eq.name);
                        const eqIdx = selected.selectedEquipment.findIndex(e => e.name === eq.name);
                        return (
                          <div
                            key={eIdx}
                            className={`flex items-center gap-2 p-2 border-2 rounded cursor-pointer transition-all text-sm ${isSelected ? 'border-green-500 bg-green-50' : 'border-[#E8E1D5] hover:border-brand-primary/30'}`}
                            onClick={() => toggleEquipment(tIdx, eq.name, eq.defaultPrice)}
                            data-testid={`equipment-${eq.name.toLowerCase().replace(/[ /()]/g, '-')}-${tIdx}`}
                          >
                            <div className={`rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-green-600' : 'bg-red-500'} text-white`}>
                              {isSelected ? <Check size={12} weight="bold" /> : <X size={12} weight="bold" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="truncate font-medium">{eq.name}</div>
                              <div className="text-xs text-text-secondary">₹{eq.defaultPrice.toLocaleString('en-IN')}</div>
                            </div>
                            {isSelected && (
                              <button
                                type="button"
                                className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${isSelected.included ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}
                                onClick={(e) => { e.stopPropagation(); toggleEquipmentIncluded(tIdx, eqIdx); }}
                                data-testid={`eq-included-${eIdx}-${tIdx}`}
                              >
                                {isSelected.included ? 'Included' : 'Extra ₹'}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Genres */}
                {genres.length > 0 && (
                  <div>
                    <div className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-2 flex items-center gap-1">
                      <MusicNote size={14} /> Genre / Style
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {genres.map((g, gIdx) => {
                        const isSelected = selected.selectedGenres.includes(g.name);
                        return (
                          <button
                            key={gIdx}
                            type="button"
                            className={`px-3 py-1.5 text-sm rounded-full border-2 transition-all ${isSelected ? 'border-green-500 bg-green-50 text-green-800 font-semibold' : 'border-[#E8E1D5] text-text-secondary hover:border-brand-primary/30'}`}
                            onClick={() => toggleGenre(tIdx, g.name)}
                            data-testid={`genre-${g.name.toLowerCase().replace(/[ /]/g, '-')}-${tIdx}`}
                          >
                            {isSelected && <Check size={12} weight="bold" className="inline mr-1" />}
                            {g.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      <div className="sticky bottom-4 bg-sand-50 p-4 border-t border-[#E8E1D5]">
        <Button type="button" onClick={handleSaveAll} disabled={selectedTypes.length === 0}
          className="w-full bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none" data-testid="save-dj-btn">
          Save {selectedTypes.length} Entertainment Type{selectedTypes.length !== 1 ? 's' : ''}
        </Button>
      </div>
    </div>
  );
};

export default DJEntertainmentSelector;
